/**
 * useNotificationSocket Hook
 *
 * Custom React hook for managing WebSocket connection to notification system.
 * Handles real-time notification delivery, connection state, and user events.
 *
 * Features:
 * - Automatic Socket.io connection with JWT authentication
 * - Real-time notification delivery
 * - Unread count tracking
 * - Connection state management
 * - Event emission (mark as read, fetch notifications)
 * - Toast notifications for new notifications
 * - Automatic reconnection
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * @example
 * const { connected, notifications, unreadCount, markAsRead } = useNotificationSocket();
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Notification socket hook
 *
 * @returns {Object} Socket state and methods
 * @property {boolean} connected - WebSocket connection status
 * @property {Array} notifications - Array of recent notifications
 * @property {number} unreadCount - Count of unread notifications
 * @property {Function} markAsRead - Mark notification as read
 * @property {Function} markAllAsRead - Mark all notifications as read
 * @property {Function} fetchNotifications - Fetch notifications with pagination
 * @property {Function} deleteNotification - Delete a notification
 */
export function useNotificationSocket() {
  const { accessToken, user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);
  const toastCallback = useRef(null);

  // ============================================================================
  // SOCKET CONNECTION MANAGEMENT
  // ============================================================================

  useEffect(() => {
    if (!accessToken || !user) {
      // Not authenticated, don't connect
      return;
    }

    console.log('[NotificationSocket] Connecting to WebSocket...');

    // Create Socket.io connection with JWT authentication
    const newSocket = io(import.meta.env.VITE_API_URL || 'http://localhost:3030', {
      auth: { token: accessToken },
      transports: ['websocket', 'polling'], // Prefer WebSocket, fallback to polling
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Connection successful
    newSocket.on('connect', () => {
      console.log('[NotificationSocket] Connected to WebSocket');
      setConnected(true);
      setError(null);

      // Fetch initial notifications on connect
      newSocket.emit('notification:fetch', { page: 1, limit: 20 });
    });

    // Connection error
    newSocket.on('connect_error', (err) => {
      console.error('[NotificationSocket] Connection error:', err.message);
      setConnected(false);
      setError(err.message);
    });

    // Disconnection
    newSocket.on('disconnect', (reason) => {
      console.log('[NotificationSocket] Disconnected:', reason);
      setConnected(false);
    });

    // ============================================================================
    // NOTIFICATION EVENTS
    // ============================================================================

    // New notification received
    newSocket.on('notification:new', (notification) => {
      console.log('[NotificationSocket] New notification received:', notification);

      // Add to notifications list
      setNotifications((prev) => [notification, ...prev]);

      // Increment unread count
      setUnreadCount((prev) => prev + 1);

      // Show toast if callback is registered
      if (toastCallback.current) {
        toastCallback.current(notification);
      }
    });

    // Notification list received
    newSocket.on('notification:list', (data) => {
      console.log('[NotificationSocket] Notification list received:', data);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    });

    // Unread count update
    newSocket.on('notification:unread-count', ({ count }) => {
      console.log('[NotificationSocket] Unread count updated:', count);
      setUnreadCount(count);
    });

    // Notification marked as read (server confirmation)
    newSocket.on('notification:read', ({ notificationId }) => {
      console.log('[NotificationSocket] Notification marked as read:', notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true, readAt: new Date() } : n))
      );
    });

    // All notifications marked as read
    newSocket.on('notification:all-read', () => {
      console.log('[NotificationSocket] All notifications marked as read');
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true, readAt: new Date() })));
      setUnreadCount(0);
    });

    // Notification deleted
    newSocket.on('notification:deleted', ({ notificationId }) => {
      console.log('[NotificationSocket] Notification deleted:', notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      console.log('[NotificationSocket] Disconnecting...');
      newSocket.disconnect();
    };
  }, [accessToken, user]);

  // ============================================================================
  // SOCKET METHODS
  // ============================================================================

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   */
  const markAsRead = useCallback(
    (notificationId) => {
      if (!socket) {return;}

      console.log('[NotificationSocket] Marking as read:', notificationId);
      socket.emit('notification:mark-read', notificationId);

      // Optimistically update UI
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    },
    [socket]
  );

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(() => {
    if (!socket) {return;}

    console.log('[NotificationSocket] Marking all as read');
    socket.emit('notification:mark-all-read');

    // Optimistically update UI
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
  }, [socket]);

  /**
   * Fetch notifications with pagination
   * @param {Object} options - Fetch options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.limit - Items per page (default: 20)
   */
  const fetchNotifications = useCallback(
    ({ page = 1, limit = 20 } = {}) => {
      if (!socket) {return;}

      console.log('[NotificationSocket] Fetching notifications:', { page, limit });
      socket.emit('notification:fetch', { page, limit });
    },
    [socket]
  );

  /**
   * Delete notification
   * @param {string} notificationId - Notification ID
   */
  const deleteNotification = useCallback(
    (notificationId) => {
      if (!socket) {return;}

      console.log('[NotificationSocket] Deleting notification:', notificationId);
      socket.emit('notification:delete', notificationId);

      // Optimistically update UI
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    },
    [socket]
  );

  /**
   * Register toast callback
   * @param {Function} callback - Callback function to show toast
   */
  const registerToastCallback = useCallback((callback) => {
    toastCallback.current = callback;
  }, []);

  // ============================================================================
  // RETURN HOOK INTERFACE
  // ============================================================================

  return {
    // Connection state
    connected,
    error,

    // Data
    notifications,
    unreadCount,

    // Methods
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    deleteNotification,
    registerToastCallback,
  };
}
